import { uuid } from './uuid';
import {
  buildDocImagesWorkerUrl,
  buildImageWorkerUrl,
  requestWorker
} from '../api/r2';
import {
  apiCreateImage,
  apiDeleteImageByKey,
  apiGetImagesForDoc,
  apiGetVersionImageKeysForVersions
} from '../api/images';
import { debug } from './debug';
import config from '../config';

const MAX_IMAGE_WIDTH = Number(config?.IMAGE?.MAX_UPLOAD_WIDTH || 2000);
const IMAGE_QUALITY = Number(config?.IMAGE?.UPLOAD_QUALITY || 0.85);
const PUBLIC_R2_ENDPOINT = (() => {
  const configured = (process.env.REACT_APP_R2_ENDPOINT || '').trim();
  if (configured) {
    return configured.replace(/\/+$/, '');
  }
  const accountId = (process.env.REACT_APP_R2_ACCOUNT_ID || '').trim();
  if (!accountId) return '';
  return `https://${accountId}.r2.cloudflarestorage.com`;
})();

const encodeKeyPath = (key = '') => String(key)
  .split('/')
  .map(part => encodeURIComponent(part))
  .join('/');

async function loadImageSource(file) {
  if (!file) {
    throw new Error('No file provided for image optimization.');
  }

  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }

  const src = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err || new Error('Unable to load image.'));
      img.src = src;
    });
  } finally {
    URL.revokeObjectURL(src);
  }
}

export async function optimizeImageForUpload(file, options = {}) {
  if (!file) throw new Error('optimizeImageForUpload requires a file.');
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('optimizeImageForUpload only accepts image files.');
  }

  const maxWidth = options.maxWidth || MAX_IMAGE_WIDTH;
  const quality = typeof options.quality === 'number' ? options.quality : IMAGE_QUALITY;

  const source = await loadImageSource(file);
  const sourceWidth = source.width || source.naturalWidth;
  const sourceHeight = source.height || source.naturalHeight;

  if (!sourceWidth || !sourceHeight) {
    if (typeof source.close === 'function') source.close();
    throw new Error('Unable to determine source image dimensions.');
  }

  const scale = Math.min(1, maxWidth / sourceWidth);
  const targetWidth = Math.max(1, Math.floor(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.floor(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, targetWidth, targetHeight);
  }

  if (typeof source.close === 'function') {
    source.close();
  }

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (!result) {
        reject(new Error('Failed to export optimized image.'));
        return;
      }
      resolve(result);
    }, 'image/webp', quality);
  });

  return {
    blob,
    width: targetWidth,
    height: targetHeight
  };
}

export const collectImageUrlsFromContent = (raw) => {
  const urls = new Set();
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'image' && node.attrs && node.attrs.src) {
      urls.add(String(node.attrs.src));
    }
    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
    if (node.json && Array.isArray(node.json.content)) {
      node.json.content.forEach(walk);
    }
  };
  walk(raw);
  return Array.from(urls);
};

export const extractImageKeyFromUrl = (url) => {
  try {
    const parsed = new URL(String(url || ''));
    const parts = parsed.pathname.split('/').filter(Boolean);

    const imagesIdx = parts.findIndex(part => part === 'images');
    if (imagesIdx >= 0 && parts.length > imagesIdx + 2) {
      return parts
        .slice(imagesIdx + 2)
        .map(segment => decodeURIComponent(segment))
        .join('/');
    }

    if (parts.length >= 2) {
      return parts
        .slice(1)
        .map(segment => decodeURIComponent(segment))
        .join('/');
    }
  } catch {
    // no-op
  }
  return null;
};

export const getDocImageKeysFromR2 = async ({ projectId, docId }) => {
  if (!projectId || !docId) return [];
  const url = buildDocImagesWorkerUrl({ projectId, docId });
  const response = await requestWorker(url, { method: 'GET' });
  if (!response.ok) return [];
  const payload = await response.json().catch(() => null);
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return items.map(item => String(item?.key || '')).filter(Boolean);
};

export const reconcileOrphanImages = async ({
  projectId,
  docId,
  keepImageUrls = [],
  purgeRetentionMs = 0,
  getVersions
} = {}) => {
  if (!projectId || !docId || typeof getVersions !== 'function') return { prunedImages: 0 };

  const existingImages = await apiGetImagesForDoc(docId).catch(() => []);
  const dbImageKeySet = new Set((existingImages || []).map(i => String(i?.key || '')).filter(Boolean));
  const r2ImageKeys = await getDocImageKeysFromR2({ projectId, docId }).catch(() => []);
  const remainingVersions = await getVersions({ projectId, docId }).catch(() => []);
  const remainingVersionIds = remainingVersions.map(v => v.versionId).filter(Boolean);
  const referencedFromVersions = remainingVersionIds.length > 0
    ? await apiGetVersionImageKeysForVersions(remainingVersionIds).catch(() => [])
    : [];

  const referencedKeys = new Set(referencedFromVersions || []);
  const urlToKey = new Map((existingImages || []).map(i => [String(i.url || ''), i.key]));
  for (const imageUrl of (keepImageUrls || [])) {
    const key = urlToKey.get(String(imageUrl || '')) || extractImageKeyFromUrl(imageUrl);
    if (key) referencedKeys.add(key);
  }

  let prunedImages = 0;
  for (const img of (existingImages || [])) {
    if (!img?.key) continue;
    if (referencedKeys.has(img.key)) continue;

    if (img.deleted_at) {
      const deletedAt = new Date(img.deleted_at).getTime();
      if (!Number.isNaN(deletedAt) && (Date.now() - deletedAt) < Number(purgeRetentionMs || 0)) {
        continue;
      }
    }

    try {
      const imageUrl = buildImageWorkerUrl({ projectId, imageKey: img.key });
      const resp = await requestWorker(imageUrl, { method: 'DELETE' });
      if (resp.ok || resp.status === 404) {
        await apiDeleteImageByKey(img.key).catch(() => null);
        prunedImages += 1;
      }
    } catch {
      // best-effort
    }
  }

  for (const key of (r2ImageKeys || [])) {
    if (!key) continue;
    if (dbImageKeySet.has(String(key))) continue;
    if (referencedKeys.has(String(key))) continue;

    try {
      const imageUrl = buildImageWorkerUrl({ projectId, imageKey: key });
      const resp = await requestWorker(imageUrl, { method: 'DELETE' });
      if (resp.ok || resp.status === 404) {
        prunedImages += 1;
      }
    } catch {
      // best-effort
    }
  }

  return { prunedImages };
};

const buildImageKey = (docId) => {
  if (!docId) {
    throw new Error('buildImageKey requires a docId.');
  }
  return `docs/${docId}/images/${uuid()}.webp`;
};

const INTERNAL_DOC_IMAGE_KEY_RE = /^docs\/([^/]+)\/images\/.+/;

const rewriteImageUrlsInDocJson = (docJson, replacements = new Map()) => {
  if (!docJson || typeof docJson !== 'object') return docJson;

  const cloned = JSON.parse(JSON.stringify(docJson));
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'image' && node.attrs && node.attrs.src) {
      const current = String(node.attrs.src);
      const replacement = replacements.get(current);
      if (replacement) {
        node.attrs.src = replacement;
      }
    }
    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };

  walk(cloned);
  return cloned;
};

export const duplicateInternalImagesForDocContent = async ({
  projectId,
  targetDocId,
  contentJson,
  uploadedBy = null
} = {}) => {
  if (!projectId || !targetDocId || !contentJson || typeof contentJson !== 'object') {
    return { contentJson, copiedCount: 0 };
  }

  const imageUrls = collectImageUrlsFromContent(contentJson);
  if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
    return { contentJson, copiedCount: 0 };
  }

  const replacements = new Map();
  const normalizedTargetDocId = String(targetDocId);

  for (const sourceUrl of imageUrls) {
    const key = extractImageKeyFromUrl(sourceUrl);
    if (!key) continue;

    const keyMatch = String(key).match(INTERNAL_DOC_IMAGE_KEY_RE);
    if (!keyMatch) continue;

    const sourceDocId = String(keyMatch[1] || '');
    if (!sourceDocId || sourceDocId === normalizedTargetDocId) continue;

    try {
      const sourceWorkerUrl = buildImageWorkerUrl({ projectId, imageKey: key });
      const readResponse = await requestWorker(sourceWorkerUrl, { method: 'GET' });
      if (!readResponse?.ok) continue;

      const contentType = readResponse.headers.get('content-type') || 'image/webp';
      const body = await readResponse.arrayBuffer();
      if (!body || body.byteLength === 0) continue;

      const copiedKey = buildImageKey(normalizedTargetDocId);
      const copiedWorkerUrl = buildImageWorkerUrl({ projectId, imageKey: copiedKey });
      const writeResponse = await requestWorker(copiedWorkerUrl, {
        method: 'PUT',
        headers: {
          'content-type': contentType,
          'cache-control': 'public, max-age=31536000, immutable'
        },
        body
      });
      if (!writeResponse?.ok) continue;

      await apiCreateImage({
        docId: normalizedTargetDocId,
        key: copiedKey,
        url: copiedWorkerUrl,
        uploadedBy: uploadedBy || null,
        metadata: {
          copied_from_key: key,
          copied_from_doc_id: sourceDocId,
          contentType
        }
      }).catch(() => null);

      replacements.set(String(sourceUrl), copiedWorkerUrl);
    } catch (err) {
      debug('[duplicateInternalImagesForDocContent] copy failed', { type: 'warn' }, err);
    }
  }

  if (replacements.size === 0) {
    return { contentJson, copiedCount: 0 };
  }

  const rewritten = rewriteImageUrlsInDocJson(contentJson, replacements);
  return {
    contentJson: rewritten,
    copiedCount: replacements.size
  };
};

const buildPublicImageUrl = ({ bucket, key }) => {
  if (!PUBLIC_R2_ENDPOINT) {
    throw new Error('Missing R2 endpoint configuration (REACT_APP_R2_ENDPOINT or REACT_APP_R2_ACCOUNT_ID).');
  }
  if (!bucket) throw new Error('buildPublicImageUrl requires a bucket');
  if (!key) throw new Error('buildPublicImageUrl requires a key');
  return `${PUBLIC_R2_ENDPOINT}/${encodeURIComponent(bucket)}/${encodeKeyPath(key)}`;
};

export const createImage = async ({ docId, file, projectId = null, uploadedBy = null }) => {
  if (!docId) {
    throw new Error('createImage requires a docId.');
  }
  if (!file) {
    throw new Error('createImage requires a file.');
  }
  if (!projectId) {
    throw new Error('createImage requires a projectId.');
  }

  const optimized = await optimizeImageForUpload(file);
  const key = buildImageKey(docId);
  const workerUrl = buildImageWorkerUrl({ projectId, imageKey: key });
  const response = await requestWorker(workerUrl, {
    method: 'PUT',
    headers: {
      'content-type': 'image/webp',
      'cache-control': 'public, max-age=31536000, immutable'
    },
    body: optimized.blob
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Image upload failed (${response.status})${errorText ? ` - ${errorText}` : ''}`);
  }

  const payload = await response.json();
  let url = null;
  if (payload?.url) {
    url = payload.url;
  } else if (payload?.bucket && payload?.key) {
    if (PUBLIC_R2_ENDPOINT) {
      url = buildPublicImageUrl({ bucket: payload.bucket, key: payload.key });
    } else {
      // Fall back to serving the image through the worker if no public R2 endpoint is configured.
      // Use the original client-side key (the `key` variable) as the imagePath portion when building
      // the worker GET URL to avoid duplicating the `projects/<id>/` prefix.
      try {
        url = buildImageWorkerUrl({ projectId, imageKey: key });
      } catch (e) {
        url = null;
      }
    }
  }

  if (!url) {
    throw new Error('Image upload succeeded but no public URL returned by worker and no R2 endpoint configured. Set REACT_APP_R2_ENDPOINT/REACT_APP_R2_ACCOUNT_ID or ensure the worker returns `url`.');
  }

  // Try to record the image in the DB (non-blocking for the upload flow).
  const recordKey = (payload && payload.key) ? payload.key : key;
  try {
    await apiCreateImage({
      docId,
      key: recordKey,
      url,
      uploadedBy: uploadedBy || null,
      metadata: {
        width: optimized.width,
        height: optimized.height,
        contentType: 'image/webp',
        originalName: file.name || null
      }
    });
  } catch (err) {
    debug('[createImage] apiCreateImage failed', { type: 'warn' }, err);
  }

  return {
    url,
    width: optimized.width,
    height: optimized.height,
    key: recordKey
  };
};
